import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  LinearProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material'
import {
  Search,
  Visibility,
  Add,
  CheckCircle,
  Cancel,
  WarningAmber,
  AssignmentTurnedIn,
  Person,
  Event,
  Settings,
} from '@mui/icons-material'
import API from '../../../services/API'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ErrorModal from '../../../components/Modals/Error'

const statusColour = (audit) => {
  if (audit.overallCompliant) return 'success'
  return 'error'
}

const formatDate = (date) => {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-GB')
}

export default function AuditLogPage() {
  const [audits, setAudits] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('All')
  const [selectedAudit, setSelectedAudit] = useState(null)
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()

  const removeQueryParam = (key) => {
    searchParams.delete(key)
    setSearchParams(searchParams)
  }

  const submitSuccess = searchParams.get('submitSuccess')

  useEffect(() => {
    API.get('/governance/audits')
      .then((res) => {
        if (res.status === 200) setAudits(res.data)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  console.log('AUDITS', audits)

  const filteredAudits = useMemo(() => {
    return audits.filter((audit) => {
      const title =
        audit.auditDescription?.title ||
        audit.auditTemplate?.title ||
        audit.title ||
        ''

      const completedBy =
        audit.completedBy?.name ||
        audit.completedBy?.fullName ||
        audit.completedBy?.email ||
        ''

      const matchesSearch =
        title.toLowerCase().includes(search.toLowerCase()) ||
        completedBy.toLowerCase().includes(search.toLowerCase())

      const matchesStatus =
        status === 'All' ||
        (status === 'Compliant' && audit.overallCompliant) ||
        (status === 'Non-compliant' && !audit.overallCompliant)

      return matchesSearch && matchesStatus
    })
  }, [audits, search, status])

  const stats = useMemo(() => {
    const total = audits.length
    const compliant = audits.filter((a) => a.overallCompliant).length
    const nonCompliant = audits.filter((a) => !a.overallCompliant).length
    const actionsRequired = audits.reduce(
      (count, audit) =>
        count + (audit.responses || []).filter((r) => r.actionRequired).length,
      0,
    )

    return { total, compliant, nonCompliant, actionsRequired }
  }, [audits])

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="stretch"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="title2">Audit Log</Typography>
          <Typography color="text.secondary">
            Review completed audits, compliance outcomes and resulting actions.
          </Typography>
        </Box>
        <Stack gap={1}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/governance/audit')}
          >
            Complete Audit
          </Button>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            size="small"
            onClick={() => navigate('/governance/audit-templates')}
          >
            Manage Templates
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <StatCard
          icon={<AssignmentTurnedIn />}
          label="Completed"
          value={stats.total}
        />
        <StatCard
          icon={<CheckCircle />}
          label="Compliant"
          value={stats.compliant}
          color="anGreen.main"
        />
        <StatCard
          icon={<Cancel />}
          label="Non-compliant"
          value={stats.nonCompliant}
          color="anRed.main"
        />
        <StatCard
          icon={<WarningAmber />}
          label="Actions Required"
          value={stats.actionsRequired}
          color="anOrange.main"
        />
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth
            placeholder="Search by audit or completed by..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {['All', 'Compliant', 'Non-compliant'].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Audit</TableCell>
                <TableCell>Last Completed</TableCell>
                <TableCell>Next Due</TableCell>
                <TableCell>Completed By</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredAudits.map((audit) => {
                const responses = audit.responses || []
                const answered = responses.filter(
                  (r) => r.compliant !== null || r.notApplicable,
                ).length

                // N/A items are excluded from scoring entirely — an audit
                // where every applicable item is compliant scores 100%.
                const scorable = responses.filter((r) => !r.notApplicable)
                const compliant = scorable.filter(
                  (r) => r.compliant === true,
                ).length
                const score = scorable.length
                  ? Math.round((compliant / scorable.length) * 100)
                  : 100

                const actions = responses.filter((r) => r.actionRequired).length

                return (
                  <TableRow
                    key={audit._id}
                    hover
                    onClick={() => setSelectedAudit(audit)}
                  >
                    <TableCell>
                      <Typography fontWeight={800}>
                        {audit.auditDescription?.title ||
                          audit.auditTemplate?.title ||
                          audit.title ||
                          'Untitled Audit'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {answered}/{responses.length} items completed
                      </Typography>
                    </TableCell>

                    <TableCell>{formatDate(audit.date)}</TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Person fontSize="small" color="disabled" />
                        <Typography variant="body2">
                          {audit.completedBy
                            ? `${audit.completedBy.firstName} ${audit.completedBy.lastName}`
                            : 'Unknown'}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ minWidth: 140 }}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight={700}>
                          {score}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={score}
                          sx={{ height: 8, borderRadius: 4 }}
                          color={
                            score === 100
                              ? 'success'
                              : score >= 80
                                ? 'warning'
                                : 'error'
                          }
                        />
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        sx={{ width: '100%' }}
                        label={
                          audit.overallCompliant ? 'Compliant' : 'Non-compliant'
                        }
                        color={statusColour(audit)}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={`${actions} action${actions === 1 ? '' : 's'}`}
                        color={actions ? 'error' : 'success'}
                        variant={actions ? 'filled' : 'outlined'}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title="View audit">
                        <IconButton onClick={() => setSelectedAudit(audit)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && filteredAudits.length === 0 && (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Event sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" fontWeight={800}>
              No audits found
            </Typography>
            <Typography color="text.secondary">
              Complete an audit or adjust your filters.
            </Typography>
          </Box>
        )}
      </Paper>

      <AuditDetailsDialog
        audit={selectedAudit}
        open={!!selectedAudit}
        onClose={() => setSelectedAudit(null)}
      />
      <ErrorModal
        success
        open={submitSuccess}
        onClose={() => {
          searchParams.delete('submitSuccess')
          setSearchParams(searchParams)
        }}
        title="Audit Submitted"
        message="Your Audit has been successfully submitted"
      />
    </Box>
  )
}

function StatCard({ icon, label, value, color }) {
  return (
    <Grid item xs={6} md={3}>
      <Card
        elevation={0}
        sx={{
          height: '100%',
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 4,
                bgcolor: color ?? 'secondary.light',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>

            <Box>
              <Typography variant="caption" fontWeight={900}>
                {value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  )
}

function AuditDetailsDialog({ open, audit, onClose }) {
  if (!audit) return null

  const responses = audit.responses || []

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography variant="caption" fontWeight={900}>
          {audit.auditDescription?.title ||
            audit.auditTemplate?.title ||
            audit.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Completed {formatDate(audit.date)}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip
            label={audit.overallCompliant ? 'Compliant' : 'Non-compliant'}
            color={audit.overallCompliant ? 'success' : 'error'}
          />
          <Chip label={`${responses.length} checklist items`} />
        </Stack>

        <Stack spacing={2}>
          {responses.map((response, index) => {
            const notApplicable =
              response?.notApplicable ||
              (response?.compliant === null &&
                response?.actionRequired === false)

            return (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: notApplicable
                    ? 'divider'
                    : response.compliant
                      ? 'success.main'
                      : 'error.main',
                }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Typography fontWeight={800}>
                      {index + 1}. {response.name}
                    </Typography>

                    <Chip
                      size="small"
                      label={
                        notApplicable
                          ? 'N/A'
                          : response.compliant
                            ? 'Compliant'
                            : 'Non-compliant'
                      }
                      color={
                        notApplicable
                          ? 'default'
                          : response.compliant
                            ? 'success'
                            : 'error'
                      }
                    />
                  </Stack>

                  {response.comment && (
                    <Typography variant="body2" color="text.secondary">
                      {response.comment}
                    </Typography>
                  )}

                  {response.actionRequired && (
                    <>
                      <Divider />
                      <Chip
                        size="small"
                        color="warning"
                        label={response.actionTitle || 'Action required'}
                        sx={{ alignSelf: 'flex-start' }}
                      />
                      {response.actionDescription && (
                        <Typography variant="body2" color="text.secondary">
                          {response.actionDescription}
                        </Typography>
                      )}
                    </>
                  )}
                </Stack>
              </Paper>
            )
          })}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
